import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  PenLine, Camera, Users, Trophy, BookOpen,
  Shield, ArrowRight, ArrowLeft, X, Sparkles,
  Feather, Heart, Globe
} from 'lucide-react';
import { Button } from "@/components/ui/button";

const STEPS = [
  {
    key: 'welcome',
    icon: PenLine,
    iconBg: 'bg-brand-gold/15',
    iconColor: 'text-brand-gold',
    title: 'Welcome to Collectively Write',
    subtitle: 'Your creative sanctuary awaits',
    body: "You've just joined a community of serious writers who value craft over popularity. Here's a quick look at what's waiting for you.",
    accent: 'border-brand-gold/30',
  },
  {
    key: 'portfolio',
    icon: Camera,
    iconBg: 'bg-brand-primary/15',
    iconColor: 'text-brand-primary',
    title: 'Build Your Portfolio',
    subtitle: 'Make it yours',
    body: "Upload a photo, write your bio, share what you're working on, and pick your favorite genres. Your profile is your writer's calling card—make it shine.",
    cta: { label: 'Go to Profile', path: '/profile' },
    accent: 'border-brand-primary/30',
  },
  {
    key: 'write',
    icon: Feather,
    iconBg: 'bg-brand-accent/15',
    iconColor: 'text-brand-accent',
    title: 'Write Your First Story',
    subtitle: 'Original words, your voice',
    body: "Our editor lets you choose fonts, add images, and craft your work exactly how you envision it. Before publishing, you'll confirm it's your original creation—because real craft matters here.",
    cta: { label: 'Start Writing', path: '/write' },
    accent: 'border-brand-accent/30',
  },
  {
    key: 'community',
    icon: Users,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    title: 'Explore the Community',
    subtitle: 'Find your people',
    body: "Discover fellow writers, follow the ones who inspire you, join writing groups, and dive into discussions. The best writing happens when you're surrounded by kindred spirits.",
    cta: { label: 'Discover Writers', path: '/discover' },
    accent: 'border-purple-200',
  },
  {
    key: 'challenges',
    icon: Trophy,
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    title: 'Enter Challenges',
    subtitle: 'Craft-based, never popularity',
    body: "Our bi-weekly challenges are judged on skill—best character development, best hook, best conceptualization. No clique-voting, no popularity contests. Just your words on their own merit.",
    cta: { label: 'View Challenges', path: '/challenges' },
    accent: 'border-amber-200',
  },
  {
    key: 'guidelines',
    icon: Shield,
    iconBg: 'bg-brand-primary/10',
    iconColor: 'text-brand-primary',
    title: 'Our Promise to You',
    subtitle: 'A safe space for creativity',
    body: "You own 100% of your work—always. We keep things respectful and focused on craft. No erotica, no politics, no AI-generated content. Just real writers growing together.",
    cta: { label: 'Read Guidelines', path: '/guidelines' },
    accent: 'border-brand-primary/20',
  },
];

const WelcomeTour = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const [exiting, setExiting] = useState(false);
  const [direction, setDirection] = useState('next');
  const navigate = useNavigate();
  const { api } = useAuth();
  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const isFirst = step === 0;
  const progress = ((step + 1) / STEPS.length) * 100;

  const completeTour = async () => {
    try {
      await api.post('/auth/complete-tour');
    } catch (e) {
      // Non-critical error suppressed
    }
    setExiting(true);
    setTimeout(() => onComplete(), 400);
  };

  const handleNext = () => {
    if (isLast) {
      completeTour();
    } else {
      setDirection('next');
      setStep(s => s + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirst) {
      setDirection('prev');
      setStep(s => s - 1);
    }
  };

  const handleSkip = () => {
    completeTour();
  };

  const handleCta = () => {
    completeTour();
    if (current.cta?.path) {
      setTimeout(() => navigate(current.cta.path), 100);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-400 ${
        exiting ? 'opacity-0' : 'opacity-100'
      }`}
      data-testid="welcome-tour-overlay"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-brand-dark/60 backdrop-blur-sm" />

      {/* Card */}
      <div
        className={`relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden border-t-4 ${current.accent} transition-all duration-300 ${
          direction === 'next' ? 'animate-fade-in' : 'animate-fade-in'
        }`}
        key={current.key}
        data-testid="welcome-tour-card"
      >
        {/* Skip button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 p-1.5 rounded-full text-text-muted hover:text-text-primary hover:bg-surface-alt transition-colors z-10"
          data-testid="tour-skip-button"
          title="Skip tour"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Progress bar */}
        <div className="h-1 bg-surface-alt">
          <div
            className="h-full bg-brand-gold transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
            data-testid="tour-progress-bar"
          />
        </div>

        {/* Content */}
        <div className="px-8 pt-10 pb-8">
          {/* Icon */}
          <div className={`w-16 h-16 rounded-2xl ${current.iconBg} flex items-center justify-center mb-6`}>
            <current.icon className={`w-8 h-8 ${current.iconColor}`} />
          </div>

          {/* Step indicator */}
          <p className="text-xs font-medium text-text-muted uppercase tracking-widest mb-2">
            Step {step + 1} of {STEPS.length}
          </p>

          {/* Title */}
          <h2 className="font-serif text-2xl font-semibold text-text-primary mb-1">
            {current.title}
          </h2>
          <p className="text-sm text-brand-accent font-medium mb-4">{current.subtitle}</p>

          {/* Body */}
          <p className="text-text-secondary leading-relaxed mb-8">
            {current.body}
          </p>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {!isFirst && (
                <Button
                  variant="ghost"
                  onClick={handlePrev}
                  className="gap-1 text-text-muted hover:text-text-primary"
                  data-testid="tour-prev-button"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Button>
              )}
            </div>

            <div className="flex items-center gap-3">
              {current.cta && (
                <Button
                  variant="outline"
                  onClick={handleCta}
                  className="btn-outline gap-1.5 text-sm"
                  data-testid="tour-cta-button"
                >
                  {current.cta.label}
                </Button>
              )}
              <Button
                onClick={handleNext}
                className="btn-accent gap-1.5"
                data-testid="tour-next-button"
              >
                {isLast ? (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Let's Write!
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Step dots */}
        <div className="flex justify-center gap-1.5 pb-6">
          {STEPS.map((s, i) => (
            <button
              key={s.key}
              onClick={() => { setDirection(i > step ? 'next' : 'prev'); setStep(i); }}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                i === step
                  ? 'w-6 bg-brand-gold'
                  : i < step
                    ? 'bg-brand-primary/40'
                    : 'bg-surface-alt'
              }`}
              data-testid={`tour-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default WelcomeTour;
